// ============================================================================
// WorkOrderPanelContext.js — Multi-panel floating work order system
// Supports opening multiple WOs simultaneously
// ============================================================================
import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

function getToken() {
  try {
    const raw = localStorage.getItem('token');
    if (raw) return raw;
    const keys = Object.keys(localStorage).filter(k => k.includes('auth-token'));
    for (const key of keys) {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) return parsed.access_token;
    }
  } catch (e) {}
  return null;
}
function headers() {
  const t = getToken();
  return t ? { Authorization: 'Bearer ' + t } : {};
}

const WorkOrderPanelContext = createContext();

export function WorkOrderPanelProvider({ children }) {
  // panels: [{ id, wo, minimized, loading }]
  const [panels, setPanels] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const openPanel = useCallback(async (woId) => {
    // If already open, just focus it
    const existing = panels.find(p => p.wo?.id === woId);
    if (existing) {
      setPanels(prev => prev.map(p => p.wo?.id === woId ? { ...p, minimized: false } : p));
      setActiveId(woId);
      return;
    }

    // Add new panel (max 5)
    const tempId = 'loading-' + woId;
    setPanels(prev => {
      const cleaned = prev.length >= 5 ? prev.slice(1) : prev;
      return [...cleaned, { id: tempId, wo: null, minimized: false, loading: true }];
    });
    setActiveId(woId);

    try {
      const res = await axios.get(`${API}/api/work-orders/${woId}`, { headers: headers() });
      const wo = res.data.work_order;
      setPanels(prev => prev.map(p => p.id === tempId ? { id: wo.id, wo, minimized: false, loading: false } : p));
      setActiveId(wo.id);
    } catch (err) {
      console.error('Failed to load work order:', err);
      setPanels(prev => prev.filter(p => p.id !== tempId));
    }
  }, [panels]);

  const closePanel = useCallback((woId) => {
    setPanels(prev => prev.filter(p => (p.wo?.id || p.id) !== woId));
    setActiveId(prev => prev === woId ? null : prev);
  }, []);

  const closeAllPanels = useCallback(() => {
    setPanels([]);
    setActiveId(null);
  }, []);

  const toggleMinimize = useCallback((woId) => {
    setPanels(prev => prev.map(p => (p.wo?.id || p.id) === woId ? { ...p, minimized: !p.minimized } : p));
  }, []);

  const focusPanel = useCallback((woId) => {
    setActiveId(woId);
    setPanels(prev => prev.map(p => (p.wo?.id || p.id) === woId ? { ...p, minimized: false } : p));
  }, []);

  const refreshPanel = useCallback(async (woId) => {
    const panel = panels.find(p => p.wo?.id === woId);
    if (!panel?.wo) return;
    try {
      const res = await axios.get(`${API}/api/work-orders/${woId}`, { headers: headers() });
      setPanels(prev => prev.map(p => p.wo?.id === woId ? { ...p, wo: res.data.work_order } : p));
    } catch (err) {}
  }, [panels]);

  const updateStatus = useCallback(async (woId, status) => {
    try {
      await axios.put(`${API}/api/work-orders/${woId}/status`, { status }, { headers: headers() });
      await refreshPanel(woId);
    } catch (err) { console.error('Status update failed'); }
  }, [refreshPanel]);

  const addLineItem = useCallback(async (woId, item) => {
    try {
      await axios.post(`${API}/api/work-orders/${woId}/items`, item, { headers: headers() });
      await refreshPanel(woId);
    } catch (err) { console.error('Add item failed'); }
  }, [refreshPanel]);

  const deleteLineItem = useCallback(async (woId, itemId) => {
    try {
      await axios.delete(`${API}/api/work-orders/${woId}/items/${itemId}`, { headers: headers() });
      await refreshPanel(woId);
    } catch (err) { console.error('Delete item failed'); }
  }, [refreshPanel]);

  const deleteWorkOrder = useCallback(async (woId) => {
    if (!window.confirm('Delete this work order?')) return;
    try {
      await axios.delete(`${API}/api/work-orders/${woId}`, { headers: headers() });
      closePanel(woId);
    } catch (err) { console.error('Delete failed'); }
  }, [closePanel]);

  // Legacy single-panel API (for backward compat — returns first panel)
  const firstPanel = panels.find(p => !p.minimized) || panels[0];

  return (
    <WorkOrderPanelContext.Provider value={{
      // Multi-panel API
      panels, activeId,
      openPanel, closePanel, closeAllPanels, toggleMinimize, focusPanel,
      refreshPanel, updateStatus, addLineItem, deleteLineItem, deleteWorkOrder,
      headers,
      // Legacy single-panel API
      panelWO: firstPanel?.wo || null,
      loading: firstPanel?.loading || false,
      minimized: firstPanel?.minimized || false,
    }}>
      {children}
    </WorkOrderPanelContext.Provider>
  );
}

export function useWorkOrderPanel() {
  return useContext(WorkOrderPanelContext);
}
